import {
  productDataTransformer,
  type Slot,
  type Bundle,
  type IProduct,
  type ProductData,
  type DerivedAvailabilityStatusInternal,
  type BundleType,
  type OptionMap,
} from '@commerce-product-webcomponents/sdk';
import type { IBundleState } from './BundleComponent';
import { Operation, SlotType } from './types/enums';
import type { BundleDataType, ProductDataType, SlotDataType, TranslationFn } from './types/propsTypes';

const isSlotTypeNotPool = (slot: Slot) => slot.type !== SlotType.POOL;
const slotQtyInitialization = (slot: Slot) => Number(isSlotTypeNotPool(slot));

export const sortBundleData = (bundle?: BundleDataType): { bundle?: BundleDataType; foundFixedIndex?: number } => {
  let newBundle = bundle;
  let foundFixedIndex = -1;
  if (newBundle) {
    const { slots } = newBundle;
    if (slots) {
      const foundSlots: SlotDataType[] = [];
      [SlotType.POOL, SlotType.VARIANT, SlotType.FIXED].forEach((slotType) => {
        slots?.forEach((slot: SlotDataType) => {
          if (slotType === slot.type) {
            foundSlots.push({
              ...slot,
              totalQty: slotQtyInitialization(slot),
              isPoolConditionMatched: isSlotTypeNotPool(slot),
              products: slot.products?.map((product) => ({
                ...product,
                quantity: slotQtyInitialization(slot),
              })),
            });
          }
        });
      });
      newBundle = { ...newBundle, slots: [...foundSlots] };
      foundFixedIndex = foundSlots.findIndex((slot: SlotDataType) => slot.type === SlotType.FIXED);
    }
  }
  return {
    bundle: newBundle,
    foundFixedIndex,
  };
};

export const getNewBundleState = (
  slot: SlotDataType,
  quantity: number | undefined,
  slotType: SlotType,
  product: ProductData,
): IBundleState => {
  const pd: IProduct = productDataTransformer(product);
  return {
    slotId: slot?.id as number,
    productId: pd.productId,
    quantity: quantity as number,
    price: pd.productPrice,
    deriveStockStatus: pd.derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
    slotType,
    totalQty: slot.totalQty,
    firstAvailableFutureDate: pd.firstAvailableFutureDate,
    retailPrice: pd.productRetailPrice,
    pointValue: Number(pd.pointValue),
    buisnessVolume: Number(pd.businessVolume),
  };
};

export const getLinearBundleState = (bundle: (Bundle | undefined) | BundleDataType): IBundleState[] => {
  const tempBundleState: IBundleState[] = [];
  bundle?.slots?.forEach((slot: SlotDataType) => {
    switch (slot.type) {
      case SlotType.FIXED:
        tempBundleState.push(getNewBundleState(slot, slot.minQty, SlotType.FIXED, slot.products?.[0] as ProductData));
        break;
      case SlotType.VARIANT:
        slot?.products?.map((product: ProductDataType) => {
          tempBundleState.push(getNewBundleState(slot, slot.minQty, SlotType.VARIANT, product));
        });
        break;
      case SlotType.POOL:
        slot?.products?.map((product: ProductDataType) => {
          tempBundleState.push(getNewBundleState(slot, product.quantity || 0, SlotType.POOL, product));
        });
        break;
      default:
    }
  });
  return tempBundleState;
};

export const replaceSlotPoolInstructionPlaceholders = (slot: SlotDataType, t: TranslationFn) => {
  let returnInstruction = '';
  if (slot.minQty == slot.maxQty) {
    returnInstruction = t('product.bundle.poolSelectInstructions');
  } else {
    returnInstruction = t('product.bundle.poolSelectBetweenInstructions');
    returnInstruction = returnInstruction.replace('{min-qty}', slot.minQty as unknown as string);
  }
  returnInstruction = returnInstruction.replace('{max-qty}', slot.maxQty as unknown as string);
  return returnInstruction;
};

export const replaceSlotVariantInstructionPlaceholders = (slot: SlotDataType, t: TranslationFn) => {
  let returnInstruction = t('product.bundle.variantSelectInstructions');
  const slotProductData = slot.products?.[0];
  const slotVariantDimensions = slotProductData?.variantDimensions?.[0].dimension;
  if (!slotVariantDimensions) {
    returnInstruction = t('product.bundle.variantSelectDefaultInstructions');
  }
  returnInstruction = returnInstruction.replace(
    '{variant-type}',
    t(`product.variant.${slotVariantDimensions}`).toLowerCase(),
  );
  return returnInstruction;
};

export const bundleTypeCheck = (bundle: Bundle | undefined, bundleType: BundleType) =>
  bundle?.type === bundleType && bundle?.slots?.length;

export const checkForAllPoolConditionMatched = (bundleObj: BundleDataType) => {
  const isPoolType = isBundlePoolType(bundleObj);
  if (!isPoolType) {
    return true;
  }
  return bundleObj?.slots?.every((slot) => slot.isPoolConditionMatched) as boolean;
};

export const operationBasedConditions = (
  operation: Operation,
  bundleDataState: BundleDataType,
  slotIndex: number,
  productId: string,
) => {
  const slots = [...(bundleDataState.slots as SlotDataType[])];
  const slotObj = slots?.[slotIndex];
  const totalQty = slotObj?.totalQty;
  const minQty = slotObj?.minQty;
  if (totalQty !== undefined && minQty !== undefined) {
    const updatedTotal = totalQty + (operation === Operation.ADD ? 1 : -1);
    slotObj.totalQty = updatedTotal;
    slotObj.isPoolConditionMatched = updatedTotal !== undefined && updatedTotal >= minQty;
  }
  const selectedProduct = slotObj?.products?.find(
    (product: ProductDataType) => product.items[0].itemNumber === productId,
  );
  const oldProductCount = selectedProduct?.quantity;
  if (oldProductCount !== undefined && selectedProduct) {
    selectedProduct.quantity = oldProductCount + (operation === Operation.ADD ? 1 : -1);
  }
  return { ...bundleDataState, slots };
};

export const updateBundleStateHandler = (
  productId: string,
  operation: Operation,
  slot: Slot,
  bundleDataState: BundleDataType,
): { isAllPoolConditionMatched: boolean; updatedBundleState: BundleDataType } => {
  let slotIndex = -1;
  const slotObj = getSelectedSlotIndex({ slots: bundleDataState?.slots || [] }, Number(slot?.id), (index) => {
    slotIndex = index;
  });
  const maxCondition = Number(slotObj?.totalQty) < Number(slotObj?.maxQty);
  const minCondition = Number(slotObj?.totalQty) > 0;
  let updatedBundle = {};
  if (operation === Operation.ADD && maxCondition) {
    updatedBundle = operationBasedConditions(Operation.ADD, bundleDataState, slotIndex, productId);
  }
  if (operation === Operation.MINUS && minCondition) {
    updatedBundle = operationBasedConditions(Operation.MINUS, bundleDataState, slotIndex, productId);
  }
  return {
    isAllPoolConditionMatched: checkForAllPoolConditionMatched(bundleDataState as BundleDataType),
    updatedBundleState: updatedBundle,
  };
};

export const updatedSelectedVariantOptionChoice = (product: ProductDataType, selectedOptionChoice: OptionMap) =>
  ({
    ...product,
    copy: selectedOptionChoice?.product?.copy,
    gallery: selectedOptionChoice?.product?.gallery,
    items: selectedOptionChoice?.product?.items,
    id: selectedOptionChoice?.product?.productId,
  }) as ProductDataType;

export const updateProductQtyVariant = (product: ProductDataType, bundle: IBundleState) => {
  const { productId, quantity } = bundle;
  if (productId === product?.items[0]?.itemNumber && bundle.slotType === SlotType.POOL) {
    return {
      ...product,
      quantity,
    };
  } else if (bundle?.slotType === SlotType.VARIANT) {
    const selectedOptionChoice = product?.variantGroup?.optionMap?.find(
      (option: OptionMap) => option?.product?.items?.[0]?.itemNumber === productId,
    ) as OptionMap;
    if (selectedOptionChoice) {
      return updatedSelectedVariantOptionChoice(product, selectedOptionChoice);
    }
    return { ...product };
  } else {
    return { ...product };
  }
};

export const getSelectedSlotIndex = (bundleObj: BundleDataType, slotId: number, cb?: (slot: number) => void) =>
  bundleObj.slots?.find((slot, index) => {
    if (slot?.id === slotId) {
      cb?.(index);
      return true;
    }
    return false;
  });

export const isBundlePoolType = (bundle: Bundle | BundleDataType | undefined) =>
  bundle?.slots?.some((slot: SlotDataType) => slot.type === SlotType.POOL);
